import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft } from "lucide-react";

const empty = { name: "", email: "", phone: "", password: "", confirm: "", platform_role: "", is_active: true };

export default function AdminUserForm() {
  const { uid } = useParams();
  const isEdit = !!uid;
  const nav = useNavigate();
  const { user } = useAuth();
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [roles, setRoles] = useState([]);

  // Role options come from the Admin Role and Permission Master (not the
  // regular user Role & Permission Master).
  useEffect(() => {
    api.get("/admin/admin-roles").then(({ data }) => setRoles(data || [])).catch(() => setRoles([]));
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    api.get("/admin/admin-users").then(({ data }) => {
      const u = data.find(x => x.id === uid);
      if (!u) { toast.error("Admin user not found"); nav("/admin"); return; }
      setF({ name: u.name, email: u.email, phone: u.phone || "", password: "", confirm: "",
             platform_role: u.platform_role || "support_admin", is_active: u.is_active !== false });
      setLoading(false);
    }).catch(() => { toast.error("Failed to load admin user"); nav("/admin"); });
  }, [uid]);

  const save = async (e) => {
    e?.preventDefault();
    if (!f.name || !f.email) { toast.error("Name and email are required"); return; }
    if (!f.platform_role) { toast.error("Select an admin role"); return; }
    if (!isEdit && !f.password) { toast.error("Password is required"); return; }
    if (f.password !== f.confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      const payload = { name: f.name, email: f.email, phone: f.phone,
                        platform_role: f.platform_role, is_active: f.is_active,
                        ...(f.password ? { password: f.password } : {}) };
      if (isEdit) await api.patch(`/admin/admin-users/${uid}`, payload);
      else await api.post("/admin/admin-users", { ...payload, password: f.password });
      toast.success(isEdit ? "Admin user updated" : "Admin user created");
      nav("/admin?tab=admins");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  if (loading) return <div className="p-8 text-muted-foreground">Loading…</div>;

  return (
    <div className="p-6 max-w-2xl" data-testid="admin-user-form-page">
      <Link to="/admin?tab=admins" data-testid="admin-user-back" className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-primary mb-4">
        <ArrowLeft className="w-3 h-3"/> Back to Admin Users
      </Link>
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">{isEdit ? "Edit Admin User" : "New Admin User"}</CardTitle>
          <CardDescription>Platform-level administrator with scoped permissions.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={save} className="space-y-4" data-testid="admin-user-form">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Full Name *</Label>
                <Input data-testid="au-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required />
              </div>
              <div>
                <Label>Email *</Label>
                <Input data-testid="au-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required />
              </div>
              <div>
                <Label>Mobile</Label>
                <Input data-testid="au-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
              </div>
              <div>
                <Label>{isEdit ? "New Password (blank = unchanged)" : "Password *"}</Label>
                <Input data-testid="au-password" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required={!isEdit} />
              </div>
              <div>
                <Label>Confirm Password</Label>
                <Input data-testid="au-confirm" type="password" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} required={!isEdit} />
              </div>
              <div>
                <Label>Admin Role *</Label>
                <Select value={f.platform_role} onValueChange={(v) => setF({ ...f, platform_role: v })}>
                  <SelectTrigger data-testid="au-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {roles.map(r => (
                      <SelectItem key={r.key} value={r.key} data-testid={`au-role-${r.key}`}>{(r.name || r.key).toUpperCase()}{r.built_in ? "" : " (custom)"}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">{roles.find(r => r.key === f.platform_role)?.description}</p>
              </div>
              <div className="flex items-end justify-between border border-border rounded-sm px-3 py-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Active</Label>
                <Switch data-testid="au-status" checked={f.is_active} onCheckedChange={(v) => setF({ ...f, is_active: v })} />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button data-testid="au-save" type="submit" disabled={busy}>{busy ? "Saving…" : isEdit ? "Save Changes" : "Create Admin User"}</Button>
              <Button data-testid="au-cancel" type="button" variant="outline" onClick={() => nav("/admin?tab=admins")}>Cancel</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
